import { useEffect } from "react";
import { useLive } from "./use-live.js";
import { storage } from "./storage.js";
import type { Settings } from "@roqeet/core";

/**
 * 테마 적용 훅 — 결정 #11/#13. 설정의 theme(system/light/dark)을 반응형으로 구독해
 * <html>에 반영한다. 설정이 바뀌면(설정 화면 토글, 향후 동기화) liveSettings 구독이
 * 즉시 재실행되어 화면이 바로 갱신된다.
 *
 * 색 팔레트는 index.css의 CSS 변수로 둔다: 기본 :root가 다크(기존 값 그대로),
 * `html.light` 클래스가 붙으면 라이트로 덮인다. 따라서 라이트일 때만 light 클래스를
 * 추가하면 된다.
 */
export function useTheme(): void {
  // 전역 설정을 반응형으로 구독(결정 #13). 변경 즉시 테마가 다시 적용된다.
  const settings = useLive<Settings | undefined>(() => storage.liveSettings(), []);
  const theme = settings?.theme ?? "dark";

  useEffect(() => {
    const root = document.documentElement;
    const apply = (dark: boolean) => {
      // 기본(:root)이 다크이므로, 라이트일 때만 light 클래스를 켠다.
      root.classList.toggle("light", !dark);
      root.style.colorScheme = dark ? "dark" : "light";
    };

    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      apply(mq.matches);
      const handler = (e: MediaQueryListEvent) => apply(e.matches);
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }

    apply(theme === "dark");
    return undefined;
  }, [theme]);
}
